import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import Balance from "./Balance";
import PreconfigSpent from "./PreconfigSpent";
import CustomSpent from "./CustomSpent";
import HypervisedSpent from "./HypervisedSpent";
import SharedSpent from "./SharedSpent";
import CloudSpent from "./CloudSpent";
import WiseSpent from "./WiseSpent";
import Payment from "./Payment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PaymentIcon from "@mui/icons-material/Payment";

const Overview = () => {
  return (
    <>
      <Helmet>
        <title>LabAsService - Billing Overview</title>
      </Helmet>
      <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
        <Card sx={{ mb: 4 }}>
          {/* Hero Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              p: { xs: 2.5, md: 3 },
            }}
          >
            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <AccountBalanceIcon color="primary" sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" component="h1">
                    Billing Overview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View billing information and spending across all lab types
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon color="primary" />
                <Payment />
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Balance Section */}
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
            <Balance />
          </Box>

          <Divider />

          {/* Lab Type Spending Cards */}
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2.5, 
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              Spending by Lab Type
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Link 
                  to="/app/preconfig/billinginfo" 
                  style={{ textDecoration: 'none', display: 'block', height: '100%' }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        '& > *': {
                          boxShadow: 6,
                        },
                      },
                    }}
                  >
                    <PreconfigSpent />
                  </Box>
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Link 
                  to="/app/custom/billinginfo" 
                  style={{ textDecoration: 'none', display: 'block', height: '100%' }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        '& > *': {
                          boxShadow: 6,
                        },
                      },
                    }}
                  >
                    <CustomSpent />
                  </Box>
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Link 
                  to="/app/hypervised/billinginfo" 
                  style={{ textDecoration: 'none', display: 'block', height: '100%' }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        '& > *': {
                          boxShadow: 6,
                        },
                      },
                    }}
                  >
                    <HypervisedSpent />
                  </Box>
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Link 
                  to="/app/shared/billinginfo" 
                  style={{ textDecoration: 'none', display: 'block', height: '100%' }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        '& > *': {
                          boxShadow: 6,
                        },
                      },
                    }}
                  >
                    <SharedSpent />
                  </Box>
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Link 
                  to="/app/cloudlab/billinginfo" 
                  style={{ textDecoration: 'none', display: 'block', height: '100%' }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        '& > *': {
                          boxShadow: 6,
                        },
                      },
                    }}
                  >
                    <CloudSpent />
                  </Box>
                </Link>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Link 
                  to="/app/wise/billinginfo" 
                  style={{ textDecoration: 'none', display: 'block', height: '100%' }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        '& > *': {
                          boxShadow: 6,
                        },
                      },
                    }}
                  >
                    <WiseSpent />
                  </Box>
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Container>
    </>
  );
};
export default Overview;
